export function pickImageFileFromList(files: Iterable<File> | FileList | null | undefined): File | null {
  if (!files) return null;
  return Array.from(files).find((file) => file.type.startsWith("image/")) ?? null;
}

export function pickImageFilesFromList(files: Iterable<File> | FileList | null | undefined): File[] {
  if (!files) return [];
  return Array.from(files).filter((file) => file.type.startsWith("image/"));
}

export function pickImageFilesFromClipboardItems(items: DataTransferItemList | null | undefined): File[] {
  if (!items) return [];
  return Array.from(items).flatMap((item) => {
    if (item.kind !== "file" || !item.type.startsWith("image/")) return [];
    const file = item.getAsFile();
    return file ? [file] : [];
  });
}

export interface PreparedImageFile {
  blob: Blob;
  dataUrl: string;
  name: string;
  objectUrl: string;
}

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const STRIPPED_PNG_CHUNKS = new Set([
  "iCCP",
  "cHRM",
  "gAMA",
  "sRGB",
  "eXIf",
  "iTXt",
  "tEXt",
  "zTXt",
  "iDOT"
]);

export async function prepareImageFile(
  file: File,
  createObjectUrl: (blob: Blob) => string = URL.createObjectURL
): Promise<PreparedImageFile> {
  const originalBytes = new Uint8Array(await file.arrayBuffer());
  const normalizedBytes = file.type === "image/png" ? stripPngMetadata(originalBytes) : originalBytes;
  const blob =
    normalizedBytes === originalBytes
      ? file
      : new Blob([copyToArrayBuffer(normalizedBytes)], { type: file.type || "image/png" });

  return {
    blob,
    dataUrl: await blobToDataUrl(blob),
    name: file.name,
    objectUrl: createObjectUrl(blob)
  };
}

export function stripPngMetadata(bytes: Uint8Array): Uint8Array {
  if (!hasPngSignature(bytes)) return bytes;
  const chunks: Uint8Array[] = [bytes.slice(0, PNG_SIGNATURE.length)];
  let changed = false;
  let offset = PNG_SIGNATURE.length;

  while (offset + 12 <= bytes.length) {
    const length = readUint32(bytes, offset);
    const chunkEnd = offset + 12 + length;
    if (chunkEnd > bytes.length) return bytes;

    const type = String.fromCharCode(...bytes.slice(offset + 4, offset + 8));
    if (STRIPPED_PNG_CHUNKS.has(type)) {
      changed = true;
    } else {
      chunks.push(bytes.slice(offset, chunkEnd));
    }

    offset = chunkEnd;
    if (type === "IEND") break;
  }

  return changed ? concatBytes(chunks) : bytes;
}

function hasPngSignature(bytes: Uint8Array): boolean {
  return PNG_SIGNATURE.every((value, index) => bytes[index] === value);
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return `data:${blob.type || "application/octet-stream"};base64,${btoa(binary)}`;
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const size = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const out = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy.buffer;
}
